import logger from "../utils/logger";
class SoundManager {
  constructor() {
    this.audioCtx = null;
    this.muted = typeof window !== "undefined" && localStorage.getItem("synexos_mute_sounds") === "true";
    this.dnd = typeof window !== "undefined" && localStorage.getItem("synexos_dnd") === "true";
    this.volume = typeof window !== "undefined" ? parseFloat(localStorage.getItem("synexos_sound_volume") || "1.0") : 1.0;
    this.lastPlayTime = {}; // Anti-spam debouncing
    this.ringInterval = null;
    this.reverbNode = null;
    this.wetGain = null;
    this.masterGain = null;
    this.ringNodes = [];
    this.preferenceListeners = new Set();

    // Keep mute / DND / volume in step with changes made in other tabs. The values were
    // previously read once at start-up, so muting one tab left the others ringing.
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("storage", (e) => this.applyExternalPreference(e));
    }
  }

  /** Apply a preference written by another tab (storage event). */
  applyExternalPreference(e) {
    if (!e || !e.key) return;
    if (e.key === "synexos_mute_sounds") {
      this.muted = e.newValue === "true";
      if (this.muted) this.stopRingtone();
    } else if (e.key === "synexos_dnd") {
      this.dnd = e.newValue === "true";
      if (this.dnd) this.stopRingtone();
    } else if (e.key === "synexos_sound_volume") {
      const v = parseFloat(e.newValue || "1.0");
      this.volume = Number.isFinite(v) ? v : 1.0;
    } else {
      return;
    }
    this.preferenceListeners.forEach((fn) => {
      try {
        fn({ muted: this.muted, dnd: this.dnd, volume: this.volume });
      } catch {
        /* ignore */
      }
    });
  }

  /** Subscribe to preference changes coming from other tabs (for UI state). */
  subscribePreferences(fn) {
    this.preferenceListeners.add(fn);
    return () => this.preferenceListeners.delete(fn);
  }

  isMuted() {
    return this.muted;
  }

  isDND() {
    return this.dnd;
  }

  getVolume() {
    return this.volume;
  }

  setMuted(muted) {
    this.muted = muted;
    if (typeof window !== "undefined") {
      localStorage.setItem("synexos_mute_sounds", muted ? "true" : "false");
    }
  }

  setDND(dnd) {
    this.dnd = dnd;
    if (typeof window !== "undefined") {
      localStorage.setItem("synexos_dnd", dnd ? "true" : "false");
    }
  }

  setVolume(vol) {
    this.volume = vol;
    if (typeof window !== "undefined") {
      localStorage.setItem("synexos_sound_volume", String(vol));
    }
  }

  getAudioContext() {
    if (typeof window === "undefined") return null;
    
    if (!this.audioCtx) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        this.audioCtx = new AudioContextClass();
        this.initReverb(this.audioCtx);
      }
    }
    
    if (this.audioCtx && this.audioCtx.state === "suspended") {
      this.audioCtx.resume();
    }
    
    return this.audioCtx;
  }

  initReverb(ctx) {
    try {
      this.masterGain = ctx.createGain();
      this.masterGain.gain.value = 0.55;
      this.masterGain.connect(ctx.destination);
      
      this.reverbNode = ctx.createConvolver();
      const len = Math.floor(ctx.sampleRate * 1.1);
      const buf = ctx.createBuffer(2, len, ctx.sampleRate);
      for (let ch = 0; ch < 2; ch++) {
        const dd = buf.getChannelData(ch);
        for (let i = 0; i < len; i++) {
          dd[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.4);
        }
      }
      this.reverbNode.buffer = buf;
      
      this.wetGain = ctx.createGain();
      this.wetGain.gain.value = 0.3;
      this.reverbNode.connect(this.wetGain);
      this.wetGain.connect(this.masterGain);
    } catch (e) {
      logger.warn("[SoundManager] Reverb initialization failed, falling back to direct master routing:", e?.message || "Unknown error");
      this.masterGain = ctx.createGain();
      this.masterGain.gain.value = 0.55;
      this.masterGain.connect(ctx.destination);
      this.reverbNode = null;
    }
  }

  mallet(ctx, freq, start, dur, vol, trackNode = false) {
    if (!this.masterGain) {
      this.initReverb(ctx);
    }
    
    const finalVol = vol * this.volume;
    const parts = [
      [1, 1, dur],
      [2, 0.42, dur * 0.55],
      [3.01, 0.22, dur * 0.32],
      [4.16, 0.09, dur * 0.2]
    ];
    
    parts.forEach(p => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq * p[0];
      
      o.connect(g);
      g.connect(this.masterGain);
      if (this.reverbNode) {
        g.connect(this.reverbNode);
      }
      
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(finalVol * p[1], start + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, start + p[2]);
      
      o.start(start);
      o.stop(start + p[2] + 0.05);
      
      if (trackNode) {
        this.ringNodes.push(o);
      }
    });
  }

  play(type) {
    if (this.muted) return;
    
    // Check DND for notification sounds
    const isNotification = ["incoming_message", "mention", "channel_invite", "thread_reply"].includes(type);
    if (isNotification && this.dnd) return;

    // Rate-limiting check for spam prevention (1.5 seconds for notifications, 3 seconds for connection statuses)
    const now = Date.now();
    const isConnection = ["connected", "disconnected", "reconnected"].includes(type);
    
    if (isNotification) {
      if (this.lastPlayTime[type] && now - this.lastPlayTime[type] < 1500) {
        return;
      }
      this.lastPlayTime[type] = now;
    } else if (isConnection) {
      if (this.lastPlayTime[type] && now - this.lastPlayTime[type] < 3000) {
        return;
      }
      this.lastPlayTime[type] = now;
    }

    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;
      const t = ctx.currentTime + 0.01;

      switch (type) {
        case "incoming_message": {
          // received sound from HTML: mallet(783.99, t, 0.45, .5) -> mallet(1046.5, t+0.10, 0.6, .5)
          this.mallet(ctx, 783.99, t, 0.45, 0.5);
          this.mallet(ctx, 1046.5, t + 0.10, 0.6, 0.5);
          break;
        }
        case "message_sent": {
          // sent sound from HTML: mallet(1046.5, t, 0.4, .5) -> mallet(1318.5, t+0.07, 0.5, .5)
          this.mallet(ctx, 1046.5, t, 0.4, 0.5);
          this.mallet(ctx, 1318.5, t + 0.07, 0.5, 0.5);
          break;
        }
        case "mention": {
          // Brighter bell sound using mallets: C6 -> E6 -> G6 ascending bright notes
          this.mallet(ctx, 1046.50, t, 0.5, 0.6);
          this.mallet(ctx, 1318.51, t + 0.05, 0.5, 0.5);
          this.mallet(ctx, 1567.98, t + 0.10, 0.7, 0.6);
          break;
        }
        case "channel_invite": {
          // Ascending mallet chime: C5 -> E5 -> G5
          this.mallet(ctx, 523.25, t, 0.5, 0.5);
          this.mallet(ctx, 659.25, t + 0.08, 0.5, 0.5);
          this.mallet(ctx, 783.99, t + 0.16, 0.7, 0.5);
          break;
        }
        case "channel_join": {
          // Quick ascending notes
          this.mallet(ctx, 523.25, t, 0.25, 0.5);
          this.mallet(ctx, 783.99, t + 0.06, 0.35, 0.5);
          break;
        }
        case "channel_leave": {
          // Quick descending notes
          this.mallet(ctx, 783.99, t, 0.25, 0.5);
          this.mallet(ctx, 523.25, t + 0.06, 0.35, 0.5);
          break;
        }
        case "upload_success": {
          // High positive notes
          this.mallet(ctx, 698.46, t, 0.3, 0.5);
          this.mallet(ctx, 880.00, t + 0.06, 0.4, 0.5);
          break;
        }
        case "upload_failed": {
          // Muted lower/dissonant notes
          this.mallet(ctx, 220.00, t, 0.3, 0.6);
          this.mallet(ctx, 180.00, t + 0.10, 0.4, 0.6);
          break;
        }
        case "emoji_reaction": {
          // Mellow pop
          this.mallet(ctx, 880.00, t, 0.18, 0.5);
          break;
        }
        case "thread_reply": {
          // Gentle warm ping
          this.mallet(ctx, 783.99, t, 0.5, 0.5);
          break;
        }
        case "connected": {
          // Single connect mallet chime (high note)
          this.mallet(ctx, 1046.5, t, 0.15, 0.4);
          break;
        }
        case "disconnected": {
          // end/disconnect sound from HTML: mallet(783.99, t, 0.4) -> mallet(659.25, t+0.10, 0.4) -> mallet(523.25, t+0.20, 0.7)
          this.mallet(ctx, 783.99, t, 0.4, 0.5);
          this.mallet(ctx, 659.25, t + 0.10, 0.4, 0.5);
          this.mallet(ctx, 523.25, t + 0.20, 0.7, 0.5);
          break;
        }
        case "reconnected": {
          // Quad-note ascending chime: C5 -> E5 -> G5 -> C6
          this.mallet(ctx, 523.25, t, 0.4, 0.5);
          this.mallet(ctx, 659.25, t + 0.06, 0.4, 0.5);
          this.mallet(ctx, 783.99, t + 0.12, 0.4, 0.5);
          this.mallet(ctx, 1046.5, t + 0.18, 0.6, 0.5);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      logger.warn("[SoundManager] Play failed:", error?.message || "Unknown error");
    }
  }

  // Play Tone fallback for backwards compatibility or custom synth beeps
  playTone(ctx, freq, type, startTime, duration, volume) {
    if (!this.masterGain) {
      this.initReverb(ctx);
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    
    gain.gain.setValueAtTime(volume * this.volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    
    osc.connect(gain);
    if (this.masterGain) {
      gain.connect(this.masterGain);
    } else {
      gain.connect(ctx.destination);
    }
    
    osc.start(startTime);
    osc.stop(startTime + duration + 0.01);
  }

  startRingtone(type) {
    this.stopRingtone();
    if (this.muted) return;

    const ctx = this.getAudioContext();
    if (!ctx) return;

    const playRingCycle = () => {
      try {
        if (ctx.state === "suspended") {
          ctx.resume();
        }
        const t = ctx.currentTime;
        if (type === "outgoing") {
          // Outgoing "kur kur": double ringback tone chirp (e.g. 400Hz + 450Hz) played via mallet reverb
          this.mallet(ctx, 400, t, 0.45, 0.4, true);
          this.mallet(ctx, 450, t, 0.45, 0.4, true);
          
          this.mallet(ctx, 400, t + 0.65, 0.45, 0.4, true);
          this.mallet(ctx, 450, t + 0.65, 0.45, 0.4, true);
        } else if (type === "incoming") {
          // Incoming melody from user HTML ringCycle
          const seq = [
            [523.25, 0.0, 0.55],
            [659.25, 0.16, 0.55],
            [783.99, 0.32, 0.6],
            [1046.5, 0.50, 0.75],
            [880, 0.84, 0.5],
            [783.99, 1.00, 0.5],
            [659.25, 1.18, 0.95],
            [523.25, 1.18, 1.05]
          ];
          seq.forEach(n => this.mallet(ctx, n[0], t + n[1], n[2], 0.5, true));
        }
      } catch (error) {
        logger.warn("[SoundManager] Ring cycle error:", error?.message || "Unknown error");
      }
    };

    // Run first cycle immediately
    playRingCycle();

    // Loop every 2.9 seconds to match user HTML ringStart
    this.ringInterval = setInterval(playRingCycle, 2900);
  }

  stopRingtone() {
    if (this.ringInterval) {
      clearInterval(this.ringInterval);
      this.ringInterval = null;
    }
    if (this.ringNodes) {
      this.ringNodes.forEach(n => {
        try {
          n.stop();
        } catch (e) {
          // already stopped or not started
        }
      });
      this.ringNodes = [];
    }
  }
}

export const soundManager = new SoundManager();
